import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ClickableAvatar } from "./ClickableAvatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Dumbbell,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Search,
  Calendar as CalendarIcon,
  Check,
  ChevronsUpDown,
  Youtube,
  FileText,
  ExternalLink,
  Pencil,
  Zap,
} from "lucide-react";
import { format } from "date-fns";
import DayDetailInline from "./workouts/DayDetailInline";
import WorkoutCalendar from "./workouts/WorkoutCalendar";
import WorkoutSessionView from "./workouts/WorkoutSessionView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

export function MemberWorkoutTab({ member }) {
  const { hasPermission, user, organization } = useAuth();
  const navigate = useNavigate();

  // ── Permission flags ──────────────────────────────────────
  const canAdd = hasPermission("add_workout");
  const canDelete = hasPermission("delete_workout");

  const [loading, setLoading] = useState(true);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const [activeTab, setActiveTab] = useState("calendar"); // calendar, structure, settings
  const [assignment, setAssignment] = useState(null);
  const [trainer, setTrainer] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [stats, setStats] = useState(null);
  const [draftAssignment, setDraftAssignment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // ── Self-workout feature ───────────────────────────────────
  const [useCustomSelfWorkoutSetting, setUseCustomSelfWorkoutSetting] =
    useState(member?.useCustomSelfWorkoutSetting || false);
  const [allowMemberSelfWorkout, setAllowMemberSelfWorkout] = useState(
    member?.allowMemberSelfWorkout || false,
  );
  const [togglingSwitch, setTogglingSwitch] = useState(false);

  useEffect(() => {
    if (member) {
      setUseCustomSelfWorkoutSetting(
        member.useCustomSelfWorkoutSetting || false,
      );
      setAllowMemberSelfWorkout(member.allowMemberSelfWorkout || false);
    }
  }, [member]);
  const [searchParams, setSearchParams] = useSearchParams();
  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const selectedDateStr = searchParams.get("date") || todayStr;
  const monthParam = searchParams.get("month");

  const initialMonth = useMemo(() => {
    if (monthParam) {
      const [y, m] = monthParam.split("-").map(Number);
      if (!isNaN(y) && !isNaN(m)) {
        return new Date(y, m - 1, 1);
      }
    }
    if (selectedDateStr) {
      const [y, m] = selectedDateStr.split("-").map(Number);
      if (!isNaN(y) && !isNaN(m)) {
        return new Date(y, m - 1, 1);
      }
    }
    return new Date();
  }, [monthParam, selectedDateStr]);

  const [currentMonth, setCurrentMonth] = useState(initialMonth);

  useEffect(() => {
    setCurrentMonth(initialMonth);
  }, [initialMonth]);

  const getScheduleForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const entry = schedule.find((s) => s.date === dateStr);
    if (
      entry &&
      entry.status !== "completed" &&
      entry.status !== "missed" &&
      entry.phase !== "rest"
    ) {
      const todayStr = format(new Date(), "yyyy-MM-dd");
      if (dateStr < todayStr) {
        return { ...entry, status: "missed" };
      }
    }
    return entry;
  };

  const selectedDay = useMemo(() => {
    if (!selectedDateStr || schedule.length === 0) return null;
    const dateObj = new Date(selectedDateStr);
    const entry = getScheduleForDate(dateObj);
    return {
      date: dateObj,
      schedule: entry || { status: "no workouts", workouts: [] },
    };
  }, [schedule, selectedDateStr]);

  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    fetchWorkoutData();
  }, [member.id]);

  const fetchWorkoutData = async () => {
    setLoading(true);
    try {
      const [res, draftRes] = await Promise.all([
        api.get(`/workouts/assignments/member/${member.id}`),
        api
          .get(`/workouts/assignments/draft/member/${member.id}`)
          .catch(() => ({ data: { assignment: null } })),
      ]);
      const allAssignments = res.data.assignments || [];
      setAssignments(allAssignments);
      setDraftAssignment(draftRes.data.assignment);

      const activeCard = res.data.assignment;
      if (activeCard) {
        setAssignment(activeCard);
        setTrainer(res.data.trainer);
        setSchedule(res.data.schedule || []);
        setStats(res.data.stats);
        setSelectedAssignmentId(activeCard.id);

        const fetchedSchedule = res.data.schedule || [];
        if (fetchedSchedule.length > 0) {
          const today = new Date();
          const todayStr = format(today, "yyyy-MM-dd");
          let matchingDay = null;
          if (selectedDateStr) {
            matchingDay = fetchedSchedule.find(
              (s) => s.date === selectedDateStr,
            );
          }
          if (!matchingDay) {
            matchingDay =
              fetchedSchedule.find((s) => s.date === todayStr) ||
              fetchedSchedule[0];
          }
          const dateObj = matchingDay ? new Date(matchingDay.date) : today;
          const dateStr = format(dateObj, "yyyy-MM-dd");
          const monthStr = format(dateObj, "yyyy-MM");
          if (
            searchParams.get("date") !== dateStr ||
            searchParams.get("month") !== monthStr
          ) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("date", dateStr);
                next.set("month", monthStr);
                return next;
              },
              { replace: true },
            );
          }
        }
      } else if (allAssignments.length > 0) {
        setSelectedAssignmentId(allAssignments[0].id);
        await fetchAssignmentDetails(allAssignments[0].id);
      } else {
        setAssignment(null);
        setTrainer(null);
        setSchedule([]);
        setStats(null);
        setSelectedAssignmentId("");
      }
    } catch (err) {
      console.error("Failed to fetch workout data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentDetails = async (assignmentId) => {
    if (!assignmentId) return;
    setFetchingDetails(true);
    try {
      const res = await api.get(`/workouts/assignments/${assignmentId}`);
      setAssignment(res.data.assignment);
      setTrainer(res.data.trainer);
      const fetchedSchedule = res.data.schedule || [];
      setSchedule(fetchedSchedule);
      setStats(res.data.stats);

      if (fetchedSchedule.length > 0) {
        const today = new Date();
        const todayStr = format(today, "yyyy-MM-dd");

        let matchingDay = null;
        if (selectedDateStr) {
          matchingDay = fetchedSchedule.find((s) => s.date === selectedDateStr);
        }
        if (!matchingDay) {
          matchingDay =
            fetchedSchedule.find((s) => s.date === todayStr) ||
            fetchedSchedule[0];
        }

        const dateObj = matchingDay ? new Date(matchingDay.date) : today;
        const dateStr = format(dateObj, "yyyy-MM-dd");
        const monthStr = format(dateObj, "yyyy-MM");
        if (
          searchParams.get("date") !== dateStr ||
          searchParams.get("month") !== monthStr
        ) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("date", dateStr);
              next.set("month", monthStr);
              return next;
            },
            { replace: true },
          );
        }
      }
    } catch (err) {
      console.error("Failed to fetch assignment details:", err);
      toast.error("Failed to load workout card details");
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleAssignmentChange = (id) => {
    setSelectedAssignmentId(id);
    fetchAssignmentDetails(id);
  };

  // ── Toggle useCustomSelfWorkoutSetting / allowMemberSelfWorkout ─────────
  const handleUpdateSettingMode = async (customVal) => {
    setTogglingSwitch(true);
    try {
      await api.put(`/workouts/member-settings/${member.id}`, {
        useCustomSelfWorkoutSetting: customVal,
        allowMemberSelfWorkout: allowMemberSelfWorkout,
      });
      setUseCustomSelfWorkoutSetting(customVal);
      toast.success(
        customVal
          ? "Switched to custom self-workout settings"
          : "Switched to global self-workout settings",
      );
    } catch (err) {
      toast.error("Failed to update setting mode");
    } finally {
      setTogglingSwitch(false);
    }
  };

  const handleToggleSelfWorkout = async (val) => {
    setTogglingSwitch(true);
    try {
      await api.put(`/workouts/member-settings/${member.id}`, {
        useCustomSelfWorkoutSetting: useCustomSelfWorkoutSetting,
        allowMemberSelfWorkout: val,
      });
      setAllowMemberSelfWorkout(val);
      toast.success(
        val
          ? "Self-workout enabled for member"
          : "Self-workout disabled for member",
      );
    } catch (err) {
      toast.error("Failed to update self-workout setting");
    } finally {
      setTogglingSwitch(false);
    }
  };

  // ── Staff updates member self-workout session ─────────────
  const handleStaffUpdateMemberSession = async (scheduleId, updatedSession) => {
    setSchedule((prev) =>
      prev.map((s) =>
        s.id === scheduleId ? { ...s, workoutSession: updatedSession } : s,
      ),
    );
    try {
      await api.put(`/workouts/schedule/${scheduleId}/member-session`, {
        workoutSession: updatedSession,
      });
    } catch (err) {
      toast.error("Failed to save changes");
    }
  };

  const handleStaffDeleteMemberSession = async (scheduleId) => {
    try {
      await api.delete(`/workouts/schedule/${scheduleId}/member-session`);
      toast.success("Workout session deleted successfully");
      setSchedule((prev) => prev.filter((s) => s.id !== scheduleId));
      setSelectedDay(null);
    } catch (err) {
      toast.error("Failed to delete session");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!assignment) return;
    if (
      !window.confirm(
        `Are you sure you want to change the status of this workout card to ${newStatus}?`,
      )
    )
      return;
    try {
      await api.put(`/workouts/assignments/${assignment.id}`, {
        status: newStatus,
      });
      toast.success(`Workout card status updated to ${newStatus}`);
      fetchWorkoutData();
    } catch (err) {
      console.error("Failed to update workout status:", err);
      toast.error("Failed to update status");
    }
  };

  const handleMarkComplete = async (scheduleId) => {
    if (
      !window.confirm(
        "Are you sure you want to mark this workout as completed? This will mark all workouts and sets as complete.",
      )
    )
      return;
    try {
      const entry = schedule.find((s) => s.id === scheduleId);
      if (entry?.workoutSession) {
        const sessionCopy = JSON.parse(
          JSON.stringify(entry.workoutSession || { sections: [] }),
        );
        const completer = {
          completedBy: user?.id || user?.userId || "staff",
          completedByName: user?.name || "Staff",
        };
        const nowStr = new Date().toISOString();

        sessionCopy.sections?.forEach((sec) => {
          sec.completed = true;
          sec.completedAt = nowStr;
          sec.completedBy = completer.completedBy;
          sec.completedByName = completer.completedByName;

          sec.workouts?.forEach((w) => {
            w.completed = true;
            w.completedAt = nowStr;
            w.completedBy = completer.completedBy;
            w.completedByName = completer.completedByName;

            if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
              const workoutDetails = entry.workouts?.find(
                (dw) => dw.id === w.workoutId,
              );
              w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
            }
            w.loggedSetDetails.forEach((s) => {
              s.completed = true;
              s.completedAt = nowStr;
              s.completedBy = completer.completedBy;
              s.completedByName = completer.completedByName;
            });
          });
        });
        await api.put(`/workouts/schedule/${scheduleId}/session`, {
          workoutSession: sessionCopy,
        });
      }
      await api.put(`/workouts/schedule/${scheduleId}/status`, {
        status: "completed",
      });
      toast.success("Workout marked as completed");
      fetchAssignmentDetails(selectedAssignmentId);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleMarkUncomplete = async (scheduleId) => {
    if (
      !window.confirm(
        "Are you sure you want to mark this workout as uncompleted?",
      )
    )
      return;
    try {
      const entry = schedule.find((s) => s.id === scheduleId);
      if (entry?.workoutSession) {
        const sessionCopy = JSON.parse(
          JSON.stringify(entry.workoutSession || { sections: [] }),
        );
        sessionCopy.sections?.forEach((sec) => {
          sec.completed = false;
          sec.completedAt = null;
          sec.completedBy = null;
          sec.completedByName = null;

          sec.workouts?.forEach((w) => {
            w.completed = false;
            w.completedAt = null;
            w.completedBy = null;
            w.completedByName = null;

            if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
              const workoutDetails = entry.workouts?.find(
                (dw) => dw.id === w.workoutId,
              );
              w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
            }
            w.loggedSetDetails.forEach((s) => {
              s.completed = false;
              s.completedAt = null;
              s.completedBy = null;
              s.completedByName = null;
            });
          });
        });
        await api.put(`/workouts/schedule/${scheduleId}/session`, {
          workoutSession: sessionCopy,
        });
      }
      await api.put(`/workouts/schedule/${scheduleId}/status`, {
        status: "upcoming",
      });
      toast.success("Workout marked as uncompleted");
      fetchAssignmentDetails(selectedAssignmentId);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteAssignment = async () => {
    if (!assignment) return;
    const isDraft = assignment.status === "draft";
    try {
      await api.delete(`/workouts/assignments/${assignment.id}`);
      toast.success(isDraft ? "Draft deleted" : "Assignment deleted");
      if (isDraft) {
        setDraftAssignment(null);
      }
      fetchWorkoutData();
    } catch (err) {
      toast.error(
        isDraft ? "Failed to delete draft" : "Failed to delete assignment",
      );
    }
  };

  const handleDeleteDraft = async () => {
    if (!draftAssignment) return;
    if (
      !window.confirm(
        "Are you sure you want to delete this draft? All progress will be lost.",
      )
    )
      return;
    try {
      await api.delete(`/workouts/assignments/${draftAssignment.id}`);
      toast.success("Draft deleted");
      setDraftAssignment(null);
      fetchWorkoutData();
    } catch (err) {
      toast.error("Failed to delete draft");
    }
  };

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("month", format(newMonth, "yyyy-MM"));
        return next;
      },
      { replace: true },
    );
  };

  const handleDateChange = (newDateStr) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", newDateStr);
        const [y, m] = newDateStr.split("-").map(Number);
        if (!isNaN(y) && !isNaN(m)) {
          next.set("month", `${y}-${String(m).padStart(2, "0")}`);
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleDayClick = (day) => {
    handleDateChange(format(day, "yyyy-MM-dd"));
  };

  const sectionsToRender = useMemo(() => {
    if (!selectedDay?.schedule) return [];
    const rawSections = selectedDay.schedule.workoutSession?.sections;
    if (rawSections && rawSections.length > 0) {
      return rawSections;
    }
    // Fallback for older schedule documents
    return [
      {
        name: "Workouts",
        workouts: (selectedDay.schedule.workoutIds || []).map((id) => ({
          workoutId: id,
        })),
      },
    ];
  }, [selectedDay]);

  const hasWorkouts = useMemo(() => {
    if (!sectionsToRender || sectionsToRender.length === 0) return false;
    return sectionsToRender.some(
      (sec) => sec.workouts && sec.workouts.length > 0,
    );
  }, [sectionsToRender]);

  const getWorkoutDetails = (workoutId) => {
    return selectedDay?.schedule?.workouts?.find((w) => w.id === workoutId);
  };

  const getLoggedSetDetails = (workout, workoutDetails) => {
    if (workout.loggedSetDetails && workout.loggedSetDetails.length > 0) {
      return workout.loggedSetDetails;
    }
    // If not set yet, initialize from suggestion setDetails
    const suggestions = workout.setDetails || workoutDetails?.setDetails || [];
    return suggestions.map((set) => ({
      ...set,
      completed: false,
    }));
  };

  const handleUpdateWorkoutSession = async (updatedSession) => {
    if (!selectedDay?.schedule) return;

    // 1. Instantly update schedule state
    setSchedule((prev) =>
      prev.map((s) =>
        s.id === selectedDay.schedule.id
          ? { ...s, workoutSession: updatedSession }
          : s,
      ),
    );

    try {
      await api.put(`/workouts/schedule/${selectedDay.schedule.id}/session`, {
        workoutSession: updatedSession,
      });

      // 2. Recalculate automatic status changes
      let allSetsCompleted = true;
      let totalSetsCount = 0;

      updatedSession.sections?.forEach((sec) => {
        sec.workouts?.forEach((w) => {
          const workoutDetails = selectedDay?.schedule?.workouts?.find(
            (dw) => dw.id === w.workoutId,
          );
          const loggedSets =
            w.loggedSetDetails || getLoggedSetDetails(w, workoutDetails);
          loggedSets.forEach((set) => {
            totalSetsCount++;
            if (!set.completed) {
              allSetsCompleted = false;
            }
          });
        });
      });

      if (totalSetsCount === 0) {
        let allWorkoutsCompleted = true;
        let totalWorkoutsCount = 0;
        updatedSession.sections?.forEach((sec) => {
          sec.workouts?.forEach((w) => {
            totalWorkoutsCount++;
            if (!w.completed) {
              allWorkoutsCompleted = false;
            }
          });
        });
        if (totalWorkoutsCount > 0) {
          allSetsCompleted = allWorkoutsCompleted;
        } else {
          allSetsCompleted = false;
        }
      }

      const currentDayStatus = selectedDay.schedule.status;
      if (allSetsCompleted && currentDayStatus !== "completed") {
        if (
          window.confirm(
            "All exercises completed! Would you like to mark this day as Completed on your calendar?",
          )
        ) {
          await api.put(
            `/workouts/schedule/${selectedDay.schedule.id}/status`,
            {
              status: "completed",
            },
          );
          toast.success("Day marked as completed!");
          fetchAssignmentDetails(selectedAssignmentId);
        }
      } else if (!allSetsCompleted && currentDayStatus === "completed") {
        if (
          window.confirm(
            "Some exercises are unchecked. Mark this day as incomplete/upcoming?",
          )
        ) {
          await api.put(
            `/workouts/schedule/${selectedDay.schedule.id}/status`,
            {
              status: "upcoming",
            },
          );
          toast.success("Day marked as upcoming");
          fetchAssignmentDetails(selectedAssignmentId);
        }
      }
    } catch (err) {
      console.error("Failed to save session updates:", err);
      toast.error("Failed to auto-save workout log");
    }
  };

  const handleToggleSetComplete = (
    sectionIdx,
    workoutIdx,
    setIdx,
    completedVal,
  ) => {
    if (!selectedDay?.schedule) return;
    if (
      !window.confirm(
        `Are you sure you want to mark this set as ${
          completedVal ? "completed" : "incomplete"
        }?`,
      )
    )
      return;
    const sessionCopy = JSON.parse(
      JSON.stringify(selectedDay.schedule.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);

    if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
      workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
    }

    workout.loggedSetDetails[setIdx].completed = completedVal;

    // Check if all sets completed
    const allSetsChecked = workout.loggedSetDetails.every((s) => s.completed);
    workout.completed = allSetsChecked;

    handleUpdateWorkoutSession(sessionCopy);
  };

  const handleToggleWorkoutComplete = (
    sectionIdx,
    workoutIdx,
    completedVal,
  ) => {
    if (!selectedDay?.schedule) return;
    if (
      !window.confirm(
        `Are you sure you want to mark this exercise as ${
          completedVal ? "completed" : "incomplete"
        }?`,
      )
    )
      return;
    const sessionCopy = JSON.parse(
      JSON.stringify(selectedDay.schedule.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);

    workout.completed = completedVal;

    if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
      workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
    }

    workout.loggedSetDetails.forEach((s) => {
      s.completed = completedVal;
    });

    handleUpdateWorkoutSession(sessionCopy);
  };

  const handleToggleSectionComplete = (sectionIdx, completedVal) => {
    if (!selectedDay?.schedule) return;
    if (
      !window.confirm(
        `Are you sure you want to mark this entire section as ${
          completedVal ? "completed" : "incomplete"
        }?`,
      )
    )
      return;
    const sessionCopy = JSON.parse(
      JSON.stringify(selectedDay.schedule.workoutSession || { sections: [] }),
    );
    const section = sessionCopy.sections[sectionIdx];

    section.workouts?.forEach((w) => {
      w.completed = completedVal;
      const workoutDetails = getWorkoutDetails(w.workoutId);
      if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
        w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
      }
      w.loggedSetDetails.forEach((s) => {
        s.completed = completedVal;
      });
    });

    handleUpdateWorkoutSession(sessionCopy);
  };

  const handleUpdateSetMetric = (
    sectionIdx,
    workoutIdx,
    setIdx,
    field,
    val,
  ) => {
    if (!selectedDay?.schedule) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(selectedDay.schedule.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);

    if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
      workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
    }

    workout.loggedSetDetails[setIdx][field] = val;
    handleUpdateWorkoutSession(sessionCopy);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 md:gap-4"
      data-testid="member-workout-tab"
    >
      {/* ── Header Controls ── */}
      {/* ── Header Controls ── */}
      <div className="relative z-0 flex flex-col md:flex-row md:items-center justify-between gap-4 pb-3 border border-border bg-secondary/5 p-3 rounded-xl">
        {assignments.length > 0 ? (
          <div className="flex flex-wrap items-center gap-3 md:gap-4 flex-1">
            <div className="flex flex-col gap-1 min-w-[240px]">
              <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                Selected Workout Card
              </label>
              <div className="flex items-center gap-2 flex-col md:flex-row ">
                <Select
                  value={selectedAssignmentId}
                  onValueChange={handleAssignmentChange}
                >
                  <SelectTrigger className="w-full h-9 bg-card font-heading text-sm font-semibold select-none uppercase tracking-wide">
                    <SelectValue placeholder="Select workout card" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignments.map((a) => (
                      <SelectItem
                        key={a.id}
                        value={a.id}
                        className="uppercase font-heading text-xs"
                      >
                        {a.cardName || "Unnamed Card"} -{" "}
                        <span className="text-[9px] text-muted-foreground">
                          {a.status}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  {assignment && (
                    <Select
                      value={assignment.status}
                      onValueChange={handleStatusChange}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-9 px-3 bg-card font-heading text-xs font-semibold select-none uppercase tracking-wide border",
                          assignment.status === "active"
                            ? "border-emerald-500/20 text-emerald-500 bg-emerald-500/5 hover:bg-emerald-500/10"
                            : assignment.status === "paused"
                              ? "border-amber-500/20 text-amber-500 bg-amber-500/5 hover:bg-amber-500/10"
                              : assignment.status === "draft"
                                ? "border-amber-500/20 text-amber-600 bg-amber-500/5 hover:bg-amber-500/10"
                                : assignment.status === "completed"
                                  ? "border-emerald-500/20 text-emerald-600 bg-emerald-500/5 hover:bg-emerald-500/10"
                                  : "border-border text-muted-foreground bg-muted hover:bg-muted/80",
                        )}
                      >
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="active"
                          className="uppercase font-heading text-xs text-emerald-600 font-semibold"
                        >
                          Activate
                        </SelectItem>
                        <SelectItem
                          value="inactive"
                          className="uppercase font-heading text-xs text-muted-foreground font-semibold"
                        >
                          Inactivate
                        </SelectItem>
                        <SelectItem
                          value="completed"
                          className="uppercase font-heading text-xs text-emerald-600 font-semibold"
                        >
                          Completed
                        </SelectItem>
                        <SelectItem
                          value="draft"
                          className="uppercase font-heading text-xs text-amber-600 font-semibold"
                        >
                          Draft
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {canAdd &&
                    assignment &&
                    (assignment.status === "draft" ? (
                      <Button
                        variant="outline"
                        className="h-9 px-3 border-amber-500/20 text-amber-600 hover:bg-amber-500/10 font-heading text-[10px] uppercase tracking-wide shrink-0"
                        onClick={() => {
                          navigate(
                            `/members/${member.id}/workouts/new?resumeDraft=true`,
                          );
                        }}
                        data-testid="resume-draft-btn"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Resume Draft
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="h-9 px-3 border-primary/20 text-primary hover:bg-primary/10 font-heading text-[10px] uppercase tracking-wide shrink-0"
                        onClick={() => {
                          navigate(
                            `/members/${member.id}/workouts/new?reassignId=${selectedAssignmentId}`,
                          );
                        }}
                        data-testid="reassign-workouts-btn"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Reassign
                      </Button>
                    ))}
                  {canDelete && assignment && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="h-9 w-9 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive shrink-0"
                      data-testid="delete-assignment-btn"
                      title="Delete Assignment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-semibold">
              Workouts
            </span>
            <span className="text-xs text-muted-foreground">
              No workout cards assigned to this member.
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0 md:mt-auto">
          {canAdd && (
            <Button
              onClick={() => navigate(`/members/${member.id}/workouts/new`)}
              className="font-heading uppercase tracking-wide text-xs h-9"
              data-testid="assign-new-workout-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Assign New Card
            </Button>
          )}
        </div>

        {/* Allow Self-Workout Split selection & Toggle */}
        <div className="flex flex-wrap items-center gap-2 md:mt-auto">
          {/* Segmented Selection */}
          <div className="flex items-center gap-1 bg-secondary/20 border border-border/50 rounded-lg p-0.5 shrink-0 select-none h-9">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleUpdateSettingMode(false)}
              className={cn(
                "h-7 px-2.5 text-[10px] font-mono uppercase tracking-wider rounded-md transition-all",
                !useCustomSelfWorkoutSetting
                  ? "bg-background text-foreground shadow-sm font-bold border border-border/30"
                  : "text-muted-foreground hover:bg-secondary/40",
              )}
              disabled={togglingSwitch}
            >
              Global Setting
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleUpdateSettingMode(true)}
              className={cn(
                "h-7 px-2.5 text-[10px] font-mono uppercase tracking-wider rounded-md transition-all",
                useCustomSelfWorkoutSetting
                  ? "bg-background text-foreground shadow-sm font-bold border border-border/30"
                  : "text-muted-foreground hover:bg-secondary/40",
              )}
              disabled={togglingSwitch}
            >
              Custom Setting
            </Button>
          </div>

          {/* Toggle status indicator/control */}
          {useCustomSelfWorkoutSetting ? (
            <div className="flex items-center gap-2 h-9 px-3 py-1 rounded-lg border border-border/50 bg-secondary/20 shrink-0 select-none">
              <Zap
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  allowMemberSelfWorkout
                    ? "text-amber-500"
                    : "text-muted-foreground",
                )}
              />
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                Self Workout
              </span>
              <Switch
                checked={allowMemberSelfWorkout}
                onCheckedChange={handleToggleSelfWorkout}
                disabled={togglingSwitch}
                className="scale-75"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 h-9 px-3 py-1 rounded-lg border border-border/50 bg-secondary/10 opacity-75 shrink-0 select-none">
              <Zap
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  organization?.allowMemberSelfWorkout
                    ? "text-amber-500"
                    : "text-muted-foreground",
                )}
              />
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/75 whitespace-nowrap">
                Global:{" "}
                {organization?.allowMemberSelfWorkout ? "Enabled" : "Disabled"}
              </span>
            </div>
          )}
        </div>
      </div>

      {assignment && (
        <div className="bg-card border border-border/80 rounded-xl p-2 shadow-sm select-none text-[11px] flex flex-col md:flex-row gap-2.5 items-stretch">
          {/* Stats Bar */}
          <div className="flex-1 flex flex-wrap items-center gap-1.5">
            {stats && (
              <div className="flex items-center gap-2 md:gap-3 bg-card border border-border/80 rounded-lg px-2.5 py-1 text-xs select-none shadow-sm md:mt-auto h-9">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-muted-foreground inline">
                    Completed:
                  </span>
                  <span className="font-black font-mono text-emerald-500">
                    {stats.completed}
                  </span>
                </div>
                <div className="h-4 w-[1px] bg-border/80" />
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-muted-foreground inline">
                    Remaining:
                  </span>
                  <span className="font-black font-mono text-amber-500">
                    {stats.remaining}
                  </span>
                </div>
                <div className="h-4 w-[1px] bg-border/80" />
                <div className="flex items-center gap-1.5">
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  <span className="text-muted-foreground inline">Missed:</span>
                  <span className="font-black font-mono text-red-500">
                    {stats.missed}
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 border border-border/80 rounded-lg px-2.5 py-2">
              <span className="bg-primary/10 text-primary font-bold uppercase text-[12px] px-2 py-0.5 rounded font-heading tracking-wider">
                Config
              </span>
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Weeks:{" "}
                <strong className="font-mono text-foreground font-semibold">
                  {assignment.numberOfWeeks || 1}
                </strong>
              </span>
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Workouts:{" "}
                <strong className="font-mono text-foreground font-semibold">
                  {assignment.numberOfDays || 0}d
                </strong>
              </span>
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Prep:{" "}
                <strong className="font-mono text-foreground font-semibold">
                  {assignment.fitnessPreparationDays || 0}d
                </strong>
              </span>
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Re-Prep:{" "}
                <strong className="font-mono text-foreground font-semibold">
                  {assignment.fitnessRePreparationDays || 0}d
                </strong>
              </span>
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Period:{" "}
                <strong className="font-mono text-foreground font-semibold">
                  {assignment.startDate} to {assignment.endDate || "N/A"}
                </strong>
              </span>
              {assignment.restDays && assignment.restDays.length > 0 && (
                <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                  Rest:{" "}
                  <strong className="uppercase text-[10px] text-foreground font-semibold">
                    {assignment.restDays.map((d) => d.slice(0, 3)).join(", ")}
                  </strong>
                </span>
              )}
              <span className="bg-secondary/50 border border-border/40 px-2 py-0.5 rounded font-medium">
                Policy:{" "}
                <strong className="text-foreground font-semibold">
                  {assignment.missedWorkoutPolicy === "skip_missed_workout"
                    ? "Skip"
                    : assignment.missedWorkoutPolicy === "move_to_next_day"
                      ? "Move Next"
                      : assignment.missedWorkoutPolicy === "trainer_decision"
                        ? "Trainer Decision"
                        : "Member Decision"}
                </strong>
              </span>
              {assignment.autoOverlap && (
                <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded text-[9px] uppercase font-heading font-black tracking-wide">
                  Auto Overlap
                </span>
              )}
              {assignment.isFlexible && (
                <span className="bg-sky-500/10 border border-sky-500/20 text-sky-600 dark:text-sky-400 px-2 py-0.5 rounded text-[9px] uppercase font-heading font-black tracking-wide">
                  Flexible
                </span>
              )}
              {assignment.allowMemberToComplete && (
                <span className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded text-[9px] uppercase font-heading font-black tracking-wide">
                  Member Completion
                </span>
              )}
              {assignment.enableTimer && (
                <span className="bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded text-[9px] uppercase font-heading font-black tracking-wide">
                  Timer Enabled
                </span>
              )}
              {trainer && (
                <div className="flex items-center gap-2 h-9 ml-2 bg-secondary/35 border border-border/30 px-2.5 py-0.5 rounded-lg select-none">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">
                      {trainer.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="leading-tight">
                    <p className="text-[7px] font-mono uppercase tracking-wider text-muted-foreground">
                      Trainer
                    </p>
                    <p className="text-[10px] font-semibold">{trainer.name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes Bar */}
          <div className="md:w-1/3 min-w-[200px] bg-secondary/15 border border-border/30 rounded p-1.5 flex items-start gap-1.5 flex-col">
            <FileText className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <div
              className="flex-1 text-[10px] text-muted-foreground leading-normal truncate overflow-y-auto max-h-[36px]"
              title={assignment.notes}
            >
              <span className="font-bold text-foreground/80 not-italic mr-1">
                Notes:
              </span>
              <span className="italic">
                {assignment.notes?.trim() || "None"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── View Tabs ── */}
      {assignment && (
        <div className="flex gap-1.5 border-b border-border pb-1 select-none">
          <Button
            variant={activeTab === "calendar" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("calendar")}
            className="text-xs font-heading uppercase tracking-wider h-8 rounded-full"
          >
            <CalendarIcon className="h-3.5 w-3.5 mr-1.5" /> Calendar View
          </Button>
          <Button
            variant={activeTab === "structure" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("structure")}
            className="text-xs font-heading uppercase tracking-wider h-8 rounded-full"
          >
            <Dumbbell className="h-3.5 w-3.5 mr-1.5" /> Card Structure
          </Button>
          <Button
            variant={activeTab === "settings" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("settings")}
            className="text-xs font-heading uppercase tracking-wider h-8 rounded-full"
          >
            <svg
              className="h-3.5 w-3.5 mr-1.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Card Settings
          </Button>
        </div>
      )}

      {/* ── Main Tab Content ── */}
      {fetchingDetails ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
      ) : assignment ? (
        <>
          {activeTab === "calendar" &&
            (assignment.status === "draft" ? (
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="flex flex-col items-center justify-center p-12 text-center max-w-md mx-auto">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4 border border-amber-500/20">
                    <CalendarIcon className="h-6 w-6 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-2 uppercase tracking-wide">
                    No Calendar Data for Drafts
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    This workout card is currently in draft mode. Calendars and
                    daily schedules are only generated for active or published
                    workout plans.
                  </p>
                  {canAdd && (
                    <Button
                      onClick={() =>
                        navigate(
                          `/members/${member.id}/workouts/new?resumeDraft=true`,
                        )
                      }
                      className="font-heading uppercase tracking-wider text-xs gap-1.5"
                    >
                      <Pencil className="h-4 w-4" /> Resume Draft & Publish
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Calendar + Side Panel */}
                <div className="relative z-0 grid grid-cols-1 lg:grid-cols-12 gap-2">
                  <div className="lg:col-span-6">
                    <WorkoutCalendar
                      schedule={schedule}
                      currentMonth={currentMonth}
                      onMonthChange={handleMonthChange}
                      selectedDateStr={selectedDateStr}
                      onSelectDate={handleDateChange}
                      isRePrepLocked={false}
                      todayStr={todayStr}
                    />
                  </div>

                  {/* Side Panel */}
                  <Card className="bg-card border-border lg:col-span-6 shadow-sm">
                    <CardHeader className="px-3 py-2 border-b border-border/40 flex flex-row items-center justify-between">
                      <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground font-black flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-primary" />
                        {selectedDay
                          ? format(selectedDay.date, "EEEE, MMM d, yyyy")
                          : "Select a Day"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-3 py-3">
                      {selectedDay &&
                      (hasWorkouts ||
                        assignment?.isFlexible ||
                        selectedDay.schedule?.isUnresolved ||
                        selectedDay.schedule?.phase === "rest") ? (
                        assignment?.cardType === "member_workout" ? (
                          // Staff view of member self-workout session
                          <WorkoutSessionView
                            session={selectedDay.schedule}
                            allowEdit={true}
                            allowComplete={true}
                            enableTimer={assignment?.enableTimer}
                            isStaff={true}
                            isMemberWorkout={true}
                            onSessionUpdate={(updatedSession) =>
                              handleStaffUpdateMemberSession(
                                selectedDay.schedule.id,
                                updatedSession,
                              )
                            }
                            onDeleteSession={() =>
                              handleStaffDeleteMemberSession(
                                selectedDay.schedule.id,
                              )
                            }
                          />
                        ) : (
                          <DayDetailInline
                            session={selectedDay.schedule}
                            allowMemberToComplete={true}
                            enableTimer={assignment?.enableTimer}
                            isStaff={true}
                            onSuccess={fetchWorkoutData}
                            hideHeader={true}
                            isFlexible={assignment?.isFlexible}
                            workoutCard={assignment?.workoutCard}
                            missedWorkoutPolicy={
                              assignment?.missedWorkoutPolicy
                            }
                            missedCount={stats?.missed || 0}
                            fitnessPreparationDays={
                              assignment?.fitnessPreparationDays
                            }
                            fitnessRePreparationDays={
                              assignment?.fitnessRePreparationDays
                            }
                          />
                        )
                      ) : (
                        <div className="text-center py-12">
                          <CalendarIcon className="h-8 w-8 mx-auto text-muted-foreground/60 mb-2" />
                          <p className="text-xs text-muted-foreground">
                            Click on a calendar day to view assigned workout
                            details
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}

          {activeTab === "structure" && (
            <Card className="bg-card border-border shadow-sm p-4">
              <WorkoutCardStructureViewer cardData={assignment} />
            </Card>
          )}

          {activeTab === "settings" && (
            <Card className="bg-card border-border shadow-sm p-4">
              <CardSettingsViewer assignment={assignment} trainer={trainer} />
            </Card>
          )}
        </>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Dumbbell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">
              No Workouts Assigned
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Assign workouts to this member to track their progress
            </p>
            {canAdd && (
              <Button
                onClick={() => navigate(`/members/${member.id}/workouts/new`)}
              >
                <Plus className="h-4 w-4 mr-2" /> Add Workout Card
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              {assignment?.status === "draft"
                ? "Delete Workout Draft?"
                : "Delete Workout Assignment?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {assignment?.status === "draft" ? (
                <>
                  This will permanently remove the workout draft. All progress
                  will be lost.
                </>
              ) : (
                <>
                  This will permanently remove all assigned workouts and the
                  entire schedule for{" "}
                  <span className="font-semibold text-foreground">
                    {member.name}
                  </span>
                  . This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteAssignment}
            >
              <Trash2 className="h-4 w-4 mr-2" />{" "}
              {assignment?.status === "draft"
                ? "Delete Draft"
                : "Delete Assignment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WorkoutRow({ workout, index }) {
  const [expanded, setExpanded] = useState(false);

  // Summarize metrics
  const setsCount = workout.setDetails?.length || workout.sets || 0;
  const metrics = workout.setDetails
    ?.map((set) => {
      if (workout.workoutMetric === "reps") {
        return `${set.reps || 10}${set.weightKg ? `@${set.weightKg}kg` : ""}`;
      } else if (workout.workoutMetric === "duration") {
        return `${set.durationMin || 1}m`;
      } else if (workout.workoutMetric === "distance") {
        return `${set.distanceKm || 1}km`;
      } else if (workout.workoutMetric === "count") {
        return `${set.count || 10}c`;
      }
      return "";
    })
    .filter(Boolean)
    .join(", ");

  const groupName =
    workout.groupNames && workout.groupNames.length > 0
      ? workout.groupNames[0]
      : workout.groupName;

  const typeName =
    workout.typeNames && workout.typeNames.length > 0
      ? workout.typeNames[0]
      : workout.workoutType;

  return (
    <div className="border border-border/50 rounded-lg bg-card hover:border-primary/30 transition-all overflow-hidden">
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 cursor-pointer select-none gap-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[10px] font-mono text-muted-foreground bg-secondary/30 w-5 h-5 rounded-full flex items-center justify-center shrink-0">
            {index + 1}
          </span>
          <div className="min-w-0">
            <h5 className="font-heading text-sm font-semibold text-foreground truncate">
              {workout.title || `Workout ${index + 1}`}
            </h5>
            <div className="flex items-center gap-1.5 mt-0.5">
              {groupName && (
                <Badge
                  variant="outline"
                  className="text-[8px] px-1.5 py-0 bg-primary/10 text-primary border-primary/20 uppercase"
                >
                  {groupName.toUpperCase()}
                </Badge>
              )}
              {typeName && (
                <Badge
                  variant="outline"
                  className="text-[8px] px-1.5 py-0 bg-purple-500/10 text-purple-500 border-purple-500/20 uppercase"
                >
                  {typeName.toUpperCase()}
                </Badge>
              )}
              {workout.difficulty && (
                <Badge
                  variant="secondary"
                  className="text-[8px] px-1.5 py-0 uppercase"
                >
                  {workout.difficulty}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono bg-secondary/40 text-foreground px-2 py-0.5 rounded-md border border-border/30">
            {setsCount} Sets {metrics && `(${metrics})`}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              expanded && "rotate-180",
            )}
          />
        </div>
      </div>

      {expanded && (
        <div className="p-3 bg-secondary/10 border-t border-border/30 space-y-3 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
          {workout.setDetails && workout.setDetails.length > 0 && (
            <div className="space-y-1.5">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                Sets Detail ({workout.sets})
              </p>
              <div className="flex overflow-x-auto gap-1.5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {workout.setDetails.map((set, sIdx) => {
                  let metricText = "";
                  if (workout.workoutMetric === "reps") {
                    metricText = `${set.reps || 10} reps ${set.weightKg ? `@ ${set.weightKg}kg` : ""}`;
                  } else if (workout.workoutMetric === "duration") {
                    metricText = `${set.durationMin || 1} min`;
                  } else if (workout.workoutMetric === "distance") {
                    metricText = `${set.distanceKm || 1} km`;
                  } else if (workout.workoutMetric === "count") {
                    metricText = `${set.count || 10} counts`;
                  }
                  return (
                    <div
                      key={sIdx}
                      className="text-[10px] font-mono bg-card px-2.5 py-1.5 border border-border/40 rounded flex items-center shrink-0 gap-1.5 select-none whitespace-nowrap"
                    >
                      <span className="font-semibold text-foreground">
                        Set {sIdx + 1}
                      </span>
                      <span className="text-muted-foreground/60">-</span>
                      <span className="text-muted-foreground">
                        {metricText}
                      </span>
                      {set.restSeconds &&
                        set.restSeconds !== "none" &&
                        Number(set.restSeconds) > 0 && (
                          <>
                            <span className="text-muted-foreground/60">•</span>
                            <span className="text-[10px] text-amber-500/70 font-semibold">
                              Rest {set.restSeconds}s
                            </span>
                          </>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {workout.instructions && (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                Instructions
              </p>
              <p className="text-muted-foreground italic bg-background/50 p-2 rounded border border-border/20">
                {workout.instructions}
              </p>
            </div>
          )}

          {/* YouTube Video Link */}
          {workout.youtubeLink && (
            <div className="flex items-center gap-1.5 mt-1 bg-red-500/5 hover:bg-red-500/10 transition-colors px-2 py-1 rounded border border-red-500/10 w-fit">
              <svg
                className="h-3.5 w-3.5 text-red-500 shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.108C19.52 3.5 12 3.5 12 3.5s-7.52 0-9.388.555A3.002 3.002 0 0 0 .502 6.163C0 8.07 0 12 0 12s0 3.93.502 5.837a3.003 3.003 0 0 0 2.11 2.108C4.48 20.5 12 20.5 12 20.5s7.52 0 9.388-.555a3.003 3.003 0 0 0 2.11-2.108C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <a
                href={workout.youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 dark:text-red-400 hover:underline flex items-center gap-0.5 font-medium"
              >
                Watch Video Tutorial
                <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
              </a>
            </div>
          )}

          {/* Attachments */}
          {workout.attachments && workout.attachments.length > 0 && (
            <div className="space-y-1">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                Attachments
              </p>
              <div className="flex flex-wrap gap-1.5">
                {workout.attachments.map((file, fIdx) => {
                  const fileUrl = file.url.startsWith("http")
                    ? file.url
                    : `${BACKEND_URL}${file.url}`;
                  return (
                    <a
                      key={fIdx}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-background/50 hover:bg-background/80 border border-border px-2 py-0.5 rounded text-muted-foreground hover:text-foreground transition-colors font-medium"
                    >
                      <FileText className="h-3 w-3 text-primary" />
                      <span className="truncate max-w-[120px]">
                        {file.name || "Attachment"}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkoutCardStructureViewer({ cardData }) {
  const card = cardData.workoutCard || {};

  const commonKeys = {
    preparation: "fitnessPreparationCommon",
    active: "activeCommon",
    repreparation: "fitnessRePreparationCommon",
    rest: "restCommon",
  };

  // Find which phases are active/have content
  const phases = [];
  if (cardData.fitnessPreparationDays > 0) {
    phases.push({
      key: "preparation",
      label: "Preparation",
      commonKey: "fitnessPreparationCommon",
    });
  }

  // Active phase is always present
  phases.push({
    key: "active",
    label: "Active Training",
    commonKey: "activeCommon",
  });

  if (cardData.fitnessRePreparationDays > 0) {
    phases.push({
      key: "repreparation",
      label: "Re-Preparation",
      commonKey: "fitnessRePreparationCommon",
    });
  }

  if (cardData.restDays && cardData.restDays.length > 0) {
    phases.push({
      key: "rest",
      label: "Rest / Recovery",
      commonKey: "restCommon",
    });
  }

  const [activePhase, setActivePhase] = useState(phases[0]?.key || "active");

  // Get active day keys count based on cardData config
  let dayCount = 0;
  if (activePhase === "preparation")
    dayCount = cardData.fitnessPreparationDays || 0;
  else if (activePhase === "repreparation")
    dayCount = cardData.fitnessRePreparationDays || 0;
  else if (activePhase === "rest") dayCount = cardData.restDays?.length || 0;
  else if (activePhase === "active")
    dayCount = 7 - (cardData.restDays?.length || 0);

  if (dayCount < 1) dayCount = 1;
  const dayKeys = Array.from({ length: dayCount }, (_, i) => `day-${i + 1}`);

  const [activeDay, setActiveDay] = useState("day-1");

  // Reset to day-1 if activeDay goes out of bounds when changing phase
  useEffect(() => {
    setActiveDay("day-1");
  }, [activePhase]);

  const globalCommonPlan = card.common || {};
  const phaseCommonPlan = card[commonKeys[activePhase]] || {};
  const daySpecificPlan = (card[activePhase] || {})[activeDay] || {};

  const hasGlobalWorkouts = globalCommonPlan.sections?.some(
    (s) => s.workouts && s.workouts.length > 0,
  );
  const hasPhaseCommonWorkouts = phaseCommonPlan.sections?.some(
    (s) => s.workouts && s.workouts.length > 0,
  );
  const hasDaySpecificWorkouts = daySpecificPlan.sections?.some(
    (s) => s.workouts && s.workouts.length > 0,
  );

  const noWorkouts =
    !hasGlobalWorkouts && !hasPhaseCommonWorkouts && !hasDaySpecificWorkouts;

  if (phases.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm font-mono select-none">
        No structured plans found for this workout card.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Phase Selector */}
      <div className="flex gap-1.5 border-b border-border pb-2 overflow-x-auto scrollbar-hide select-none">
        {phases.map((p) => (
          <Button
            key={p.key}
            variant={activePhase === p.key ? "default" : "ghost"}
            size="sm"
            className="text-xs rounded-full whitespace-nowrap"
            onClick={() => setActivePhase(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {/* Day Selector */}
      {dayKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center bg-secondary/10 p-2 rounded-lg border border-border/40 select-none">
          <span className="text-[10px] font-heading font-black uppercase text-muted-foreground mr-2">
            Days:
          </span>
          {dayKeys.map((dayKey) => {
            const dayNum = dayKey.replace("day-", "");
            const isActive = activeDay === dayKey;
            return (
              <Button
                key={dayKey}
                variant={isActive ? "outline" : "ghost"}
                size="sm"
                className={cn(
                  "h-7 px-2.5 text-xs font-mono rounded-md border-border",
                  isActive &&
                    "bg-primary/10 border-primary/45 text-primary font-bold shadow-sm",
                )}
                onClick={() => setActiveDay(dayKey)}
              >
                Day {dayNum}
              </Button>
            );
          })}
        </div>
      )}

      {/* Combined Compact Lists */}
      <div className="space-y-5">
        {noWorkouts ? (
          <div className="text-center py-8 text-muted-foreground text-xs font-mono select-none">
            No workouts scheduled for this day.
          </div>
        ) : (
          <>
            {/* 1. Global Common */}
            {hasGlobalWorkouts && (
              <div className="space-y-2.5">
                {/* <h4 className="text-xs font-heading font-black uppercase tracking-wider text-purple-500 border-b border-border/40 pb-1 flex justify-between select-none">
                  Global Common Workouts
                </h4> */}
                <div className="space-y-2">
                  {globalCommonPlan.sections.map((section) => (
                    <div key={section.id} className="space-y-2">
                      {section.name && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/80 font-semibold px-1">
                          {section.name}
                        </div>
                      )}
                      <div className="space-y-1.5">
                        {section.workouts
                          ?.filter((w) => w.title || w.workoutId)
                          .map((workout, idx) => (
                            <WorkoutRow
                              key={workout.clientId || idx}
                              workout={workout}
                              index={idx}
                            />
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2. Phase Common */}
            {hasPhaseCommonWorkouts && (
              <div className="space-y-2.5">
                {/* <h4 className="text-xs font-heading font-black uppercase tracking-wider text-blue-500 border-b border-border/40 pb-1 flex justify-between select-none">
                  {phases.find((p) => p.key === activePhase)?.label} Phase
                  Common Workouts
                </h4> */}
                <div className="space-y-2">
                  {phaseCommonPlan.sections.map((section) => (
                    <div
                      key={section.id}
                      className="space-y-2 bg-secondary/10 p-2 rounded-lg border border-border/40 select-none"
                    >
                      {section.name && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/80 font-semibold px-1">
                          {section.name}
                        </div>
                      )}
                      <div className="space-y-1.5">
                        {section.workouts
                          ?.filter((w) => w.title || w.workoutId)
                          .map((workout, idx) => (
                            <WorkoutRow
                              key={workout.clientId || idx}
                              workout={workout}
                              index={idx}
                            />
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. Day Specific */}
            {hasDaySpecificWorkouts && (
              <div className="space-y-2.5">
                {/* <h4 className="text-xs font-heading font-black uppercase tracking-wider text-emerald-500 border-b border-border/40 pb-1 flex justify-between select-none">
                  Day Specific Workouts
                </h4> */}
                <div className="space-y-2">
                  {daySpecificPlan.sections.map((section) => (
                    <div
                      key={section.id}
                      className="space-y-2 bg-secondary/10 p-2 rounded-lg border border-border/40 select-none"
                    >
                      {section.name && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/80 font-semibold px-1">
                          {section.name}
                        </div>
                      )}
                      <div className="space-y-1.5">
                        {section.workouts
                          ?.filter((w) => w.title || w.workoutId)
                          .map((workout, idx) => (
                            <WorkoutRow
                              key={workout.clientId || idx}
                              workout={workout}
                              index={idx}
                            />
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── CardSettingsViewer ──────────────────────────────────────────────────────────────────────────────
function CardSettingsViewer({ assignment, trainer }) {
  const restDays = assignment.restDays || [];
  const missedWorkoutPolicy =
    assignment.missedWorkoutPolicy || "skip_missed_workout";

  const policyLabelMap = {
    skip_missed_workout: "Skip Missed Workout",
    move_to_next_day: "Move to Next Day",
  };

  const getWeekDaysStr = (days) => {
    if (days.length === 0) return "None";
    return days.map((d) => d.charAt(0).toUpperCase() + d.slice(1)).join(", ");
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 select-none">
      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Card Name
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {assignment.cardName || "Unnamed Card"}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Duration
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {assignment.numberOfWeeks || 1} Weeks (
              {assignment.numberOfDays || 0} Workout Days)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Schedule Period
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {assignment.startDate} to {assignment.endDate || "N/A"}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Missed Workout Policy
            </p>
            <p className="font-semibold text-xs mt-0.5">
              {policyLabelMap[missedWorkoutPolicy] || missedWorkoutPolicy}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Rest Days
            </p>
            <p className="font-semibold text-xs mt-0.5 truncate max-w-[180px]">
              {getWeekDaysStr(restDays)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Preparation Phase
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {assignment.fitnessPreparationDays || 0} Days
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3m-3-3v12"
              />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Re-Preparation Phase
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {assignment.fitnessRePreparationDays || 0} Days
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border hover:shadow-sm transition-all duration-200 col-span-1 sm:col-span-2 md:col-span-1">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold">
              Trainer Assigned
            </p>
            <p className="font-semibold text-sm mt-0.5">
              {trainer?.name || "No trainer assigned"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── WorkoutSelect ──────────────────────────────────────────────────────────────────────────────────
function WorkoutSelect({ workouts, selectedIds, onSelect, onRemove }) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const handleUnselect = (workoutId) => {
    onRemove(workoutId);
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectedIds.forEach((id) => onRemove(id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex min-h-[42px] h-auto w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-all hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary cursor-text",
            open && "ring-2 ring-primary/20 border-primary",
          )}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex flex-wrap gap-1.5 items-center flex-1 min-w-0">
            {selectedIds.length > 0 &&
              selectedIds.map((workoutId) => {
                const workout = workouts.find((w) => w.id === workoutId);
                return (
                  <Badge
                    key={workoutId}
                    variant="secondary"
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/20 text-primary border-primary/30 gap-1 hover:bg-primary/30 transition-colors animate-in fade-in zoom-in-95 duration-150"
                  >
                    {workout?.title || workoutId}
                    <button
                      type="button"
                      className="rounded-full outline-none focus:ring-1 focus:ring-primary ml-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(workoutId);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(workoutId);
                      }}
                    >
                      <X className="h-3 w-3 text-primary/70 hover:text-primary transition-colors" />
                    </button>
                  </Badge>
                );
              })}
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder={selectedIds.length === 0 ? "Select workouts..." : ""}
              className="ml-1 flex-1 bg-transparent outline-none placeholder:text-muted-foreground text-foreground min-w-[80px]"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0 pl-2">
            {selectedIds.length > 0 && (
              <X
                className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                onClick={handleClearAll}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border border-border rounded-xl overflow-hidden"
        align="start"
        style={{
          width: triggerWidth > 0 ? triggerWidth : "auto",
          minWidth: "240px",
        }}
      >
        <Command
          className="border-none w-full"
          filter={(value, search) => {
            const workout = workouts.find((w) => String(w.id) === value);
            if (!workout) return 0;
            const q = search.toLowerCase();
            if (
              workout.title?.toLowerCase().includes(q) ||
              workout.shortDescription?.toLowerCase().includes(q)
            ) {
              return 1;
            }
            return 0;
          }}
        >
          <div className="flex items-center border-b px-3 bg-muted/30">
            <CommandInput
              placeholder="Search workouts..."
              className="border-none focus:ring-0"
              value={inputValue}
              onValueChange={setInputValue}
            />
          </div>
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-6 text-center text-muted-foreground text-sm">
              No workouts found.
            </CommandEmpty>
            <CommandGroup className="p-1 mb-1">
              {workouts.map((workout) => {
                const isSelected = selectedIds.includes(workout.id);
                return (
                  <CommandItem
                    key={workout.id}
                    value={String(workout.id)}
                    onSelect={() => {
                      if (isSelected) {
                        onRemove(workout.id);
                      } else {
                        onSelect(workout.id);
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors mb-1 rounded-md",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted",
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className={cn(
                          "h-8 w-8 rounded-md flex items-center justify-center shrink-0 overflow-hidden",
                          isSelected
                            ? "bg-primary-foreground/10"
                            : "bg-secondary",
                        )}
                      >
                        {workout.image ? (
                          <img
                            src={`${BACKEND_URL}${workout.image}`}
                            alt={workout.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Dumbbell
                            className={cn(
                              "h-4 w-4",
                              isSelected
                                ? "text-primary-foreground"
                                : "text-muted-foreground",
                            )}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-sm font-medium truncate leading-tight",
                            isSelected
                              ? "text-primary-foreground"
                              : "text-foreground",
                          )}
                        >
                          {workout.title}
                        </p>
                        {workout.shortDescription && (
                          <p
                            className={cn(
                              "text-[11px] truncate mt-0.5",
                              isSelected
                                ? "text-primary-foreground/75"
                                : "text-muted-foreground",
                            )}
                          >
                            {workout.shortDescription}
                          </p>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <Check className="h-4 w-4 text-primary-foreground shrink-0 ml-2" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {selectedIds.length} selected
              </span>
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs text-destructive hover:text-destructive/80 font-semibold transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
            >
              Done
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── AssignWorkoutModal ─────────────────────────────────────────────────────────────────────────────────────
function AssignWorkoutModal({
  open,
  onOpenChange,
  member,
  existingAssignment,
  existingTrainer,
  onSuccess,
}) {
  const [trainers, setTrainers] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [numberOfDays, setNumberOfDays] = useState(30);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [autoOverlap, setAutoOverlap] = useState(false);
  const [allowMemberToComplete, setAllowMemberToComplete] = useState(false);

  const defaultSchedule = {
    sunday: { enabled: false, useGroup: false, groupId: "", workoutIds: [] },
    monday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    tuesday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    wednesday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    thursday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    friday: { enabled: true, useGroup: false, groupId: "", workoutIds: [] },
    saturday: { enabled: false, useGroup: false, groupId: "", workoutIds: [] },
  };

  const [weeklySchedule, setWeeklySchedule] = useState(defaultSchedule);

  useEffect(() => {
    if (open) {
      fetchData();
      if (existingAssignment) {
        setSelectedTrainer(existingAssignment.trainerId || "");
        setNumberOfDays(existingAssignment.numberOfDays || 30);
        setStartDate(
          existingAssignment.startDate || format(new Date(), "yyyy-MM-dd"),
        );
        setAutoOverlap(existingAssignment.autoOverlap || false);
        setAllowMemberToComplete(
          existingAssignment.allowMemberToComplete || false,
        );
        if (existingAssignment.weeklySchedule) {
          const newSchedule = { ...defaultSchedule };
          DAYS_OF_WEEK.forEach(({ key }) => {
            const dayWorkouts = existingAssignment.weeklySchedule[key] || [];
            newSchedule[key] = {
              enabled: dayWorkouts.length > 0,
              useGroup: false,
              groupId: "",
              workoutIds: dayWorkouts,
            };
          });
          setWeeklySchedule(newSchedule);
        }
      } else {
        setSelectedTrainer("");
        setNumberOfDays(30);
        setStartDate(format(new Date(), "yyyy-MM-dd"));
        setAutoOverlap(false);
        setAllowMemberToComplete(false);
        setWeeklySchedule(defaultSchedule);
      }
    }
  }, [open, existingAssignment]);

  const fetchData = async () => {
    try {
      const [trainersRes, workoutsRes, groupsRes] = await Promise.all([
        api.get("/workouts/trainers/list"),
        api.get("/workouts"),
        api.get("/workouts/groups"),
      ]);
      setTrainers(trainersRes.data.trainers || []);
      setWorkouts(workoutsRes.data.workouts || []);
      setGroups(groupsRes.data.groups || []);
    } catch (err) {
      toast.error("Failed to load data");
    }
  };

  const handleDayToggle = (day) =>
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled },
    }));

  const handleUseGroupToggle = (day) =>
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        useGroup: !prev[day].useGroup,
        groupId: "",
        workoutIds: [],
      },
    }));

  const handleGroupSelect = (day, groupId) => {
    const groupWorkouts = workouts
      .filter((w) => w.groupIds?.includes(groupId))
      .map((w) => w.id);
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], groupId, workoutIds: groupWorkouts },
    }));
  };

  const handleWorkoutAdd = (day, workoutId) =>
    setWeeklySchedule((prev) => {
      const current = prev[day].workoutIds || [];
      if (!current.includes(workoutId))
        return {
          ...prev,
          [day]: { ...prev[day], workoutIds: [...current, workoutId] },
        };
      return prev;
    });

  const handleWorkoutRemove = (day, workoutId) =>
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        workoutIds: (prev[day].workoutIds || []).filter(
          (id) => id !== workoutId,
        ),
      },
    }));

  const handleSubmit = async () => {
    const schedule = {};
    DAYS_OF_WEEK.forEach(({ key }) => {
      const dayConfig = weeklySchedule[key];
      if (dayConfig.enabled && dayConfig.workoutIds.length > 0)
        schedule[key] = dayConfig.workoutIds;
    });
    if (Object.keys(schedule).length === 0) {
      toast.error("Please select at least one workout for any day");
      return;
    }
    setLoading(true);
    try {
      await api.post("/workouts/assignments", {
        memberId: member.id,
        trainerId:
          selectedTrainer && selectedTrainer !== "none"
            ? selectedTrainer
            : null,
        weeklySchedule: schedule,
        numberOfDays: parseInt(numberOfDays),
        startDate,
        autoOverlap,
        allowMemberToComplete,
      });
      toast.success("Workouts assigned successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign workouts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide">
            {existingAssignment ? "Reassign Workouts" : "Assign Workouts"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 space-y-2 md:px-6 md:py-6  ">
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-xl">
            <ClickableAvatar
              src={member.photo}
              name={member.name}
              className="h-12 w-12"
              fallbackClassName="bg-primary text-primary-foreground"
            />
            <div>
              <p className="font-heading font-semibold">{member.name}</p>
              <p className="text-sm text-muted-foreground">{member.email}</p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-heading uppercase">
              Weekly Schedule
            </Label>
            <div className="flex flex-col gap-3">
              {DAYS_OF_WEEK.map(({ key, label }) => (
                <div
                  key={key}
                  className={`px-3 py-2 rounded-xl border ${
                    weeklySchedule[key].enabled
                      ? "border-primary/30 bg-primary/5"
                      : "border-border bg-secondary/20"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={weeklySchedule[key].enabled}
                        onCheckedChange={() => handleDayToggle(key)}
                        id={`day-${key}`}
                      />
                      <label
                        htmlFor={`day-${key}`}
                        className="font-heading font-semibold cursor-pointer"
                      >
                        {label}day
                      </label>
                      {weeklySchedule[key].enabled &&
                        weeklySchedule[key].workoutIds.length > 0 && (
                          <Badge className="bg-primary/15 text-primary border-primary/20 border text-[10px] font-mono">
                            {weeklySchedule[key].workoutIds.length}
                          </Badge>
                        )}
                    </div>
                    {weeklySchedule[key].enabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Assign by Group
                        </span>
                        <Switch
                          checked={weeklySchedule[key].useGroup}
                          onCheckedChange={() => handleUseGroupToggle(key)}
                        />
                      </div>
                    )}
                  </div>
                  {weeklySchedule[key].enabled && (
                    <div className="space-y-3">
                      {weeklySchedule[key].useGroup && (
                        <Select
                          value={weeklySchedule[key].groupId || "select"}
                          onValueChange={(v) =>
                            v !== "select" && handleGroupSelect(key, v)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select workout group" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select" disabled>
                              Select workout group
                            </SelectItem>
                            {groups.map((g) => (
                              <SelectItem key={g.id} value={g.id}>
                                {g.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <WorkoutSelect
                        workouts={workouts}
                        selectedIds={weeklySchedule[key].workoutIds || []}
                        onSelect={(workoutId) =>
                          handleWorkoutAdd(key, workoutId)
                        }
                        onRemove={(workoutId) =>
                          handleWorkoutRemove(key, workoutId)
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">
                Number of Workout Weeks
              </Label>
              <Input
                type="number"
                value={numberOfDays}
                onChange={(e) => setNumberOfDays(e.target.value)}
                min={1}
                max={365}
              />
              <p className="text-xs text-muted-foreground">
                Total workout days to complete
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">
                Start Date
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase">
              Assign Trainer
            </Label>
            <Select
              value={selectedTrainer || "none"}
              onValueChange={setSelectedTrainer}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select trainer (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Trainer</SelectItem>
                {trainers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} ({t.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
            <div>
              <p className="font-medium text-sm">Auto Calculate Overlap Days</p>
              <p className="text-xs text-muted-foreground">
                If member misses a workout, system pushes schedule forward
              </p>
            </div>
            <Switch checked={autoOverlap} onCheckedChange={setAutoOverlap} />
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
            <div>
              <p className="font-medium text-sm">
                Allow Member to Complete Workouts
              </p>
              <p className="text-xs text-muted-foreground">
                Allow the member to mark workouts as completed in their portal
              </p>
            </div>
            <Switch
              checked={allowMemberToComplete}
              onCheckedChange={setAllowMemberToComplete}
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border shrink-0 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading
              ? "Assigning..."
              : existingAssignment
                ? "Reassign"
                : "Assign Workouts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── DraftWorkoutViewer ─────────────────────────────────────────────────────────────────────────────
function DraftWorkoutViewer({ draft, onResume, onDelete }) {
  const card = draft.workoutCard || {};

  // Find which phases are active/have content
  const phases = [];
  if (
    draft.fitnessPreparationDays > 0 &&
    card.fitnessPreparation &&
    Object.keys(card.fitnessPreparation).length > 0
  ) {
    phases.push({ key: "preparation", label: "Preparation" });
  }
  if (card.active && Object.keys(card.active).length > 0) {
    phases.push({ key: "active", label: "Active Training" });
  }
  if (
    draft.fitnessRePreparationDays > 0 &&
    card.fitnessRePreparation &&
    Object.keys(card.fitnessRePreparation).length > 0
  ) {
    phases.push({ key: "repreparation", label: "Re-Preparation" });
  }
  if (card.rest && Object.keys(card.rest).length > 0) {
    phases.push({ key: "rest", label: "Rest / Recovery" });
  }

  const [activePhase, setActivePhase] = useState(phases[0]?.key || "active");

  // For each phase, we can have day tabs
  const phasePlans = card[activePhase] || {};
  const dayKeys = Object.keys(phasePlans).sort((a, b) => {
    const na = parseInt(a.replace("day-", ""), 10);
    const nb = parseInt(b.replace("day-", ""), 10);
    return na - nb;
  });

  const [activeDay, setActiveDay] = useState(dayKeys[0] || "day-1");

  // Reset activeDay when phase changes
  useEffect(() => {
    const nextDayKeys = Object.keys(card[activePhase] || {});
    if (nextDayKeys.length > 0) {
      nextDayKeys.sort((a, b) => {
        const na = parseInt(a.replace("day-", ""), 10);
        const nb = parseInt(b.replace("day-", ""), 10);
        return na - nb;
      });
      setActiveDay(nextDayKeys[0]);
    }
  }, [activePhase, card]);

  const selectedDayPlan = phasePlans[activeDay] || { sections: [] };

  return (
    <Card className="bg-card border-border shadow-md">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border p-4 gap-3 bg-secondary/5">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-heading uppercase tracking-wide">
              {draft.cardName || "Workout Plan"}
            </CardTitle>
            <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/20 uppercase font-mono text-[10px] tracking-wide">
              Draft
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Created on {format(new Date(draft.createdAt), "MMM d, yyyy")} •{" "}
            {draft.numberOfWeeks} Weeks
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={onResume}
            size="sm"
            className="text-xs uppercase font-heading tracking-wide"
          >
            Resume Editing
          </Button>
          <Button
            onClick={onDelete}
            size="sm"
            variant="outline"
            className="text-xs uppercase font-heading tracking-wide text-destructive hover:bg-destructive/10"
          >
            Delete Draft
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {phases.length > 0 ? (
          <>
            {/* Phase Selector */}
            {phases.length > 1 && (
              <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto scrollbar-hide">
                {phases.map((p) => (
                  <Button
                    key={p.key}
                    variant={activePhase === p.key ? "default" : "ghost"}
                    size="sm"
                    className="text-xs rounded-full"
                    onClick={() => setActivePhase(p.key)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            )}

            {/* Day Selector */}
            {dayKeys.length > 0 ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs font-heading uppercase text-muted-foreground mr-1">
                    Days:
                  </span>
                  {dayKeys.map((dayKey) => {
                    const dayNum = dayKey.replace("day-", "");
                    const isActive = activeDay === dayKey;
                    return (
                      <Button
                        key={dayKey}
                        variant={isActive ? "outline" : "ghost"}
                        size="sm"
                        className={cn(
                          "h-8 px-3 text-xs font-mono rounded-md border-border",
                          isActive &&
                            "bg-primary/5 border-primary/45 text-primary",
                        )}
                        onClick={() => setActiveDay(dayKey)}
                      >
                        Day {dayNum}
                      </Button>
                    );
                  })}
                </div>

                {/* Sections & Workouts */}
                <div className="space-y-4">
                  {selectedDayPlan.sections &&
                  selectedDayPlan.sections.length > 0 ? (
                    selectedDayPlan.sections.map((section) => (
                      <div
                        key={section.id}
                        className="space-y-2 border border-border/60 rounded-xl p-3 bg-secondary/5"
                      >
                        <h4 className="text-xs font-heading font-black uppercase tracking-wider text-muted-foreground border-b border-border/40 pb-1">
                          {section.name || "Workout Section"}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {section.workouts && section.workouts.length > 0 ? (
                            section.workouts.map((workout, index) => (
                              <Card
                                key={workout.clientId || index}
                                className="bg-card border-border/80 p-3 space-y-2 hover:shadow-sm transition-all"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h5 className="font-heading text-sm font-semibold">
                                      {workout.title || `Workout ${index + 1}`}
                                    </h5>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {(workout.typeNames &&
                                      workout.typeNames.length > 0
                                        ? workout.typeNames[0]
                                        : workout.workoutType) && (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] px-1.5 py-0 bg-purple-500/10 text-purple-500 border border-purple-500/20"
                                        >
                                          {workout.typeNames &&
                                          workout.typeNames.length > 0
                                            ? workout.typeNames[0]
                                            : workout.workoutType}
                                        </Badge>
                                      )}
                                      {workout.difficulty && (
                                        <Badge
                                          variant="secondary"
                                          className="text-[9px] px-1.5 py-0 uppercase"
                                        >
                                          {workout.difficulty}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {workout.instructions && (
                                  <p className="text-xs text-muted-foreground bg-secondary/15 p-2 rounded-md italic">
                                    {workout.instructions}
                                  </p>
                                )}

                                {/* Sets list */}
                                {workout.setDetails &&
                                  workout.setDetails.length > 0 && (
                                    <div className="space-y-1">
                                      <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                                        Sets ({workout.sets})
                                      </p>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                                        {workout.setDetails.map((set, sIdx) => {
                                          let metricText = "";
                                          if (
                                            workout.workoutMetric === "reps"
                                          ) {
                                            metricText = `${set.reps || 10} reps ${set.weightKg ? `@ ${set.weightKg}kg` : ""}`;
                                          } else if (
                                            workout.workoutMetric === "duration"
                                          ) {
                                            metricText = `${set.durationMin || 1} min`;
                                          } else if (
                                            workout.workoutMetric === "distance"
                                          ) {
                                            metricText = `${set.distanceKm || 1} km`;
                                          } else if (
                                            workout.workoutMetric === "count"
                                          ) {
                                            metricText = `${set.count || 10} count`;
                                          }
                                          return (
                                            <div
                                              key={sIdx}
                                              className="text-[10px] font-mono bg-secondary/20 px-2 py-1 rounded border border-border/40 flex flex-col justify-center"
                                            >
                                              <span className="font-semibold">
                                                Set {sIdx + 1}
                                              </span>
                                              <span className="text-muted-foreground text-[9px]">
                                                {metricText}
                                              </span>
                                              {set.restSeconds &&
                                                set.restSeconds !== "none" &&
                                                Number(set.restSeconds) > 0 && (
                                                  <span className="text-[8px] text-amber-500/70">
                                                    Rest: {set.restSeconds}s
                                                  </span>
                                                )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                              </Card>
                            ))
                          ) : (
                            <p className="text-xs text-muted-foreground col-span-2">
                              No exercises drafted in this section
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No sections drafted for this day
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No days configured for this phase
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">
            No workouts drafted in this plan yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}
